/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { Grid, Stack, FormControlLabel, Switch, Typography, CircularProgress } from "@mui/material";
import { H4 } from "components/generals/_Titles";
import FormikForm from "components/layout/FormikForm";
import { CoreAPI_URLs } from "constants/http.const";
import { Form, FormikProps } from "formik";
import PaymentIcon from "@mui/icons-material/Payment";
import useHttp from "hooks/http.hook";
import { FormProps } from "interfaces";
import {
  PayerInterface,
  HeaderEnum,
  PatientInterface,
  ProviderInterface,
  InsuranceInterface,
  QueryResultInterface,
  AccountingInterface,
} from "medicalsystem-shared";
import { useEffect, useMemo, useState, SyntheticEvent } from "react";
import {
  EntityPermissions,
  FormActionType,
  PermissionsUser,
  AutoCompleteOption,
} from "types";
import {
  payerFormYUPValidation,
  validatePhoneInputSymbol,
} from "utils/form.utils";
import { Method } from "axios";
import { useTranslation } from "react-i18next";
import FormField from "components/generals/FormInput";
import Button from "components/generals/Button";
import FormActionButtons from "./custom/FormActionButtons";
import FormPhoneInput from "components/generals/FormPhoneInput";
import PermissionWrapper from "components/generals/PermissionWrapper";
import Autocomplete from "components/generals/Autocomplete";
import { calculateAge } from "utils/date.utils";
import { selectIsPatientOrDoctor } from "store/slices/auth.slice";
import { useSelector } from "react-redux";
import Box from "components/generals/Box";
import AccountingTable from "components/tables/AccountingTable";
import { useResetPageOnFilter } from "hooks/tableReset.hook";

/**
 * Represents an existing entity that can be linked as a payer.
 */
interface ExistingEntity {
  id: string;
  type: "patient" | "provider" | "insurance";
  name?: string;
  lastname?: string;
  country?: string;
  state?: string;
  age?: number;
  phone?: string;
}

/**
 * Payer form component for managing billing payers (patients, providers, or insurance companies).
 * 
 * Features:
 * - Create/edit payers with complete contact and address information
 * - Toggle between individual payer and insurance company modes
 * - Automatic entity type switching based on lastname field
 * - Link to existing patients, providers, or insurance companies
 * - Autocomplete search for existing entities
 * - Phone/fax validation and formatting
 * - Country, state, city, ZIP code management
 * - Active/inactive status toggle
 * - Display associated accounting records in embedded table
 * - Pagination and filtering for accounting transactions
 * - Permission-based form controls (read-only for patients/doctors)
 * - Automatic person/provider/insurance association
 * 
 * Payer Types:
 * - Individual: Patient or provider with first/last name
 * - Insurance: Company with only name field
 * 
 * Accounting Integration:
 * - Displays all accounting movements for selected payer
 * - Shows transaction history with pagination
 * - Filters accounting records by payer ID
 * - Auto-refreshes on filter changes
 * 
 * Security:
 * - Permission-based access control via PermissionWrapper
 * - Read-only mode for patients and doctors
 * - Validates entity permissions for edit/delete
 * - Uses HeaderEnum.RESOURCE_ID for secure identification
 * 
 * @param {FormProps<PayerInterface>} props - Form properties
 * @param {PayerInterface} props.model - Payer data to edit or empty model for new
 * @param {FormActionType} props.action - Form action (NEW, MODIFY, READ_ONLY)
 * @param {Function} props.onCloseAction - Callback when form closes
 * @param {Function} props.setModelList - Callback to update payer list
 * @param {PayerInterface[]} props.modelList - Current list of payers
 * 
 * @returns {JSX.Element} Payer form with entity linking and accounting display
 */
const PayerForm: React.FC<FormProps<PayerInterface>> = ({
  model,
  action,
  onCloseAction,
  setModelList,
  modelList,
}) => {
  const [isLoading, _error, sendRequest] = useHttp();
  const [isLoadingAccounting, _errorAccounting, sendRequestAccounting] = useHttp();
  const { t } = useTranslation();
  const [patients, setPatients] = useState<PatientInterface[]>([]);
  const [providers, setProviders] = useState<ProviderInterface[]>([]);
  const [insurances, setInsurances] = useState<InsuranceInterface[]>([]);
  const [existingEntities, setExistingEntities] = useState<ExistingEntity[]>([]);

  const [accountingList, setAccountingList] = useState<AccountingInterface[]>([]);
  const [page, setPage] = useState<number>(1);
  const [pageTotal, setPageTotal] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [rowsTotal, setRowsTotal] = useState<number>(0);
  const [filterModel, setFilterModel] = useState<string>("");

  const [isInitialAccountingLoad, setIsInitialAccountingLoad] = useState<boolean>(true);

  const isReadOnlyUser = useSelector(selectIsPatientOrDoctor);
  const isView = (action === FormActionType.READ_ONLY) || isReadOnlyUser;
  const isNew = action === FormActionType.NEW;

  const isModelInsurance = useMemo(() => {
    return model && (!model.lastname || model.lastname.trim() === "");
  }, [model]);

  const [isInsuranceMode, setIsInsuranceMode] = useState<boolean>(isModelInsurance || false);
  
  const [formValues, setFormValues] = useState<Partial<PayerInterface>>({
    id: model?.id || "",
    name: model?.name || "",
    lastname: model?.lastname || "",
    country: model?.country || "",
    state: model?.state || "",
    age: model?.age || 0,
    phone: model?.phone || ""
  });

  const fetchAccountingsByPayer = () => {
    if (!model?.id) {
      setAccountingList([]);
      setRowsTotal(0);
      setPageTotal(1);
      setIsInitialAccountingLoad(false);
      return;
    }

    const nameParam = filterModel ? `name=${filterModel}&` : "";
    const url = `${CoreAPI_URLs.ACCOUNTING_BY_PAYER}?${nameParam}page=${page}&pageSize=${pageSize}`;
    
    sendRequestAccounting(
      url,
      onGetAccountingHandler,
      "GET",
      undefined,
      [{ header: HeaderEnum.RESOURCE_ID, value: model.id }]
    );
  };

  useResetPageOnFilter(filterModel, page, setPage);

  const onGetAccountingHandler = (response: QueryResultInterface<AccountingInterface>) => {
    setAccountingList(response.results || []);
    setRowsTotal(response.total || 0);
    setPageTotal(Math.ceil((response.total || 0) / pageSize));
    setIsInitialAccountingLoad(false);
  };

  useEffect(() => {
    if (model?.id && !isNew) {
      fetchAccountingsByPayer();
    } else {
      setIsInitialAccountingLoad(false);
    }
  }, [model?.id, page, pageSize, filterModel, isNew]);

  useEffect(() => {
    if (filterModel && page !== 1) {
      setPage(1);
    }
  }, [filterModel]);

  useEffect(() => {
    if (model) {
      const shouldBeInsuranceMode = !model.lastname || model.lastname.trim() === "";
      setIsInsuranceMode(shouldBeInsuranceMode);

      setFormValues({
        id: model.id || "",
        name: model.name || "",
        lastname: model.lastname || "",
        country: model.country || "",
        state: model.state || "",
        age: shouldBeInsuranceMode ? undefined : (model.age || 0),
        phone: model.phone || ""
      });
      
      setIsInitialAccountingLoad(true);
    }
  }, [model]);

  useEffect(() => {
    if (!isView) {
      sendRequest(
        CoreAPI_URLs.PATIENTS_GET_ALL, 
        (data: QueryResultInterface<PatientInterface>) => {
          setPatients(data.results);
        }, 
        "GET"
      );

      sendRequest(
        CoreAPI_URLs.PROVIDER_GET_ALL, 
        (data: QueryResultInterface<ProviderInterface>) => {
          setProviders(data.results);
        }, 
        "GET"
      );

      sendRequest(
        CoreAPI_URLs.INSURANCE_GET_ALL, 
        (data: QueryResultInterface<InsuranceInterface>) => {
          setInsurances(data.results);
        }, 
        "GET"
      );
    }
  }, [isView]);

  useEffect(() => {
    if (isView) return;

    const entities: ExistingEntity[] = [];

    if (!isInsuranceMode) {
      patients.forEach((patient) => {
        if (patient.person) {
          let calculatedAge = 0;
          if (patient.person.dob) {
              calculatedAge = calculateAge(new Date(patient.person.dob));
          }
          
          const phoneNumber = patient.person.phones && patient.person.phones.length > 0
            ? patient.person.phones[0].number
            : "";
          
          entities.push({
            id: patient.id,
            type: "patient",
            name: patient.person.name,
            lastname: patient.person.lastname,
            country: patient.person.country,
            state: patient.person.state,
            age: calculatedAge,
            phone: phoneNumber,
          });
        }
      });

      providers.forEach((provider) => {
        if (provider.person) {
          let calculatedAge = 0;
          if (provider.person.dob) {
            try {
              calculatedAge = calculateAge(new Date(provider.person.dob));
            } catch (error) {
            }
          }
          
          const phoneNumber = provider.person.phones && provider.person.phones.length > 0
            ? provider.person.phones[0].number
            : "";
            
          entities.push({
            id: provider.id,
            type: "provider",
            name: provider.person.name,
            lastname: provider.person.lastname,
            country: provider.person.country,
            state: provider.person.state,
            age: calculatedAge,
            phone: phoneNumber,
          });
        }
      });
    } else {
      insurances.forEach((insurance) => {
        entities.push({
          id: insurance.id,
          type: "insurance",
          name: insurance.name,
          country: insurance.country || "",
          state: insurance.state || "",
          phone: insurance.phone || "",
        });
      });
    }

    setExistingEntities(entities);
  }, [patients, providers, insurances, isInsuranceMode, isView]);

  const entityOptions = useMemo<AutoCompleteOption[]>(() => {
    return existingEntities.map((entity) => ({
      id: entity.id,
      label: entity.type === "insurance" 
        ? `${entity.name} (${t("payerForm.insurance")})`
        : `${entity.name} ${entity.lastname || ""} (${
            entity.type === "patient"
              ? t("payerForm.patient")
              : t("payerForm.provider")
          })`,
    }));
  }, [existingEntities, t]);
  
  const handleEntitySelection = (
    _event: SyntheticEvent<Element, Event>,
    value: AutoCompleteOption | null,
    formikProps: FormikProps<Partial<PayerInterface>>
  ) => {
    if (!value || isView) {
      return;
    }

    const entityId = value.id; 
    const foundEntity = existingEntities.find(entity => entity.id === entityId);

    if (foundEntity) {
      
      const entityAge = foundEntity.age !== undefined && foundEntity.age !== null 
        ? foundEntity.age 
        : 0;
        
      const entityPhone = foundEntity.phone || "";
      
      const updatedValues = {
        ...formValues,
        name: foundEntity.name || "",
        lastname: isInsuranceMode ? "" : (foundEntity.lastname || ""),
        country: foundEntity.country || "",
        state: foundEntity.state || "",
        age: isInsuranceMode ? undefined : entityAge,
        phone: entityPhone,
      };
      
      setFormValues(updatedValues);

      formikProps.setValues({
        ...updatedValues,
        phone: entityPhone
      }, false);
    }
  };

  const handleInsuranceModeToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isView) {
      return;
    }

    const isChecked = event.target.checked;
    setIsInsuranceMode(isChecked);

    const resetValues = {
      id: model?.id || "",
      name: "",
      lastname: isChecked ? "" : model?.lastname || "",
      country: "",
      state: "",
      age: isChecked ? undefined : (model?.age || 0),
      phone: ""
    };
    setFormValues(resetValues);
  };

  const submitHandler = (payerData: Partial<PayerInterface>) => {
    if (isView) return;

    const method: Method = isNew ? "POST" : "PUT";
    const successMessageText = isNew ? t("payerForm.payerCreated") : t("payerForm.payerUpdated");

    if (payerData.phone) {
      if (payerData.phone.length < 3) {
        delete payerData["phone"];
      } else {
        payerData.phone = validatePhoneInputSymbol(payerData.phone);
      }
    }
    
    if (isInsuranceMode) {
      payerData.lastname = "";
      payerData.age = undefined;
    }
    
    sendRequest(
      CoreAPI_URLs.PAYER_GENERAL_OPERATIONS,
      onPayerUpdateHandler,
      method,
      JSON.stringify(payerData),
      [{ header: HeaderEnum.RESOURCE_ID, value: payerData.id }],
      { successMessage: successMessageText }
    );
  };

  const onPayerUpdateHandler = (payer: PayerInterface) => {
    if (action === FormActionType.MODIFY) {
      setModelList(modelList.map((model) => (model.id === payer.id ? payer : model)));
    } else {
      setModelList((oldArray) => [...oldArray, payer]);
    }

    onCloseAction();
  };

  const getFormTitle = () => {
    return (isView || !isNew) ? t("payerForm.viewTitle") : t("payerForm.title");
  };

  const getInsuranceModeDisplayValue = () => {
    return isInsuranceMode ? t("payerForm.insuranceMode") : t("payerForm.personMode");
  };

  return (
    <>
      <Box sx={{ mx: 'auto', px: 2 }}>
        <FormikForm
          initialValues={formValues}
          onSubmit={(values) => {
            submitHandler(values);
          }}
          validationSchema={payerFormYUPValidation(t, isInsuranceMode)}
          enableReinitialize={true}
        >
          {(formikProps: FormikProps<Partial<PayerInterface>>) => {
            const { values, errors, touched, setFieldValue } = formikProps;
            return (
              <Form id="payerForm" autoComplete="true">
                <Stack alignItems="center" direction="row" gap={2} mb={3}>
                  <PaymentIcon fontSize="large" />
                  <H4>{getFormTitle()}</H4>
                </Stack>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12}> 
                    <Stack spacing={0.5}>
                      {!isView && (
                        <>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: "text.secondary", 
                              fontSize: "0.875rem"
                            }}
                          >
                            {t("payerForm.payerTypeInstructions")}
                          </Typography>
                          <FormControlLabel
                            control={
                              <Switch
                                name="payerMode"
                                checked={isInsuranceMode}
                                onChange={handleInsuranceModeToggle}
                                color="primary"
                                disabled={isView}
                              />
                            }
                            label={isInsuranceMode ? t("payerForm.insuranceMode") : t("payerForm.personMode")}
                          />
                        </>
                      )}
                      {isView && (
                        <FormField
                          type="text"
                          label={t("payerForm.payerType")}
                          name="payerTypeDisplay"
                          value={getInsuranceModeDisplayValue()}
                          disabled={true}
                          InputProps={{
                            readOnly: true,
                          }}
                        />
                      )}
                    </Stack>
                  </Grid>
                </Grid>   
                {(isNew || action === FormActionType.MODIFY) && !isView && (
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12}>
                      <Autocomplete
                        options={entityOptions}
                        label={
                          isInsuranceMode 
                            ? t("payerForm.searchExistingInsurances") 
                            : t("payerForm.searchExistingPersons")
                        }
                        formikOutside={true}
                        onChange={(e, value) => handleEntitySelection(e, value, formikProps)}
                        noOptionsText={t("payerForm.noOptions")}
                      />
                    </Grid>
                  </Grid>
                )}
                <Grid container spacing={2}>
                  <Grid item xs={12} md={isInsuranceMode ? 12 : 6}>
                    <FormField
                      type="text"
                      label={t("payerForm.name")}
                      name="name"
                      value={values.name || ""}
                      error={errors.name && touched.name}
                      disabled={isView}
                      InputProps={{
                        readOnly: isView,
                      }}
                    />
                  </Grid>
                  {!isInsuranceMode && (
                    <Grid item xs={12} md={6}>
                      <FormField
                        type="text"
                        label={t("payerForm.lastname")}
                        name="lastname"
                        value={values.lastname || ""}
                        error={errors.lastname && touched.lastname}
                        disabled={isView}
                        InputProps={{
                          readOnly: isView,
                        }}
                      />
                    </Grid>
                  )}
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormField
                      type="text"
                      label={t("payerForm.country")}
                      name="country"
                      value={values.country || ""}
                      error={errors.country && touched.country}
                      disabled={isView}
                      InputProps={{
                        readOnly: isView,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormField
                      type="text"
                      label={t("payerForm.state")}
                      name="state"
                      value={values.state || ""}
                      error={errors.state && touched.state}
                      disabled={isView}
                      InputProps={{
                        readOnly: isView,
                      }}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  {!isInsuranceMode && (
                    <Grid item xs={12} md={6}>
                      <FormField
                        type="number"
                        label={t("payerForm.age")}
                        name="age"
                        value={values.age !== undefined ? values.age : ""}
                        error={errors.age && touched.age}
                        disabled={isView}
                        InputProps={{
                          readOnly: isView,
                        }}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} md={6}>
                    {isView ? (
                      <FormField
                        type="text"
                        label={t("payerForm.phone")}
                        name="phone"
                        value={formValues.phone || ""}
                        disabled={true}
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                    ) : (
                      <FormPhoneInput
                        inputClass="phone"
                        specialLabel={t("payerForm.phone")}
                        country={"us"}
                        required={true}
                        name="phone"
                        value={values.phone || ""}
                        error={errors.phone && touched.phone}
                        disabled={false}
                      />
                    )}
                  </Grid>
                </Grid>
                <br />
                {!isView && (
                  <div>
                    {isNew ? (
                      <PermissionWrapper
                        requiredPermissions={[PermissionsUser[EntityPermissions.PAYER].CREATE]}
                      >
                        <Button type="submit" variant="contained" color="info" disabled={isLoading}>
                          {t("stepperFormActionButtons.submit")}
                        </Button>
                      </PermissionWrapper>
                    ) : (
                      <PermissionWrapper
                        requiredPermissions={[PermissionsUser[EntityPermissions.PAYER].EDIT]}
                      >
                        <Button type="submit" variant="contained" color="info" disabled={isLoading}>
                          {t("stepperFormActionButtons.update")}
                        </Button>
                      </PermissionWrapper>
                    )}
                  </div>
                )}
                {model?.id && !isNew && (
                  <Box style={{ marginTop: '2rem', position: 'relative' }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      {t('payerForm.relatedAccountings')}
                    </Typography>
                    {isLoadingAccounting && !isInitialAccountingLoad && (
                      <Box 
                        sx={{ 
                          position: 'absolute', 
                          top: 0, 
                          left: 0, 
                          width: '100%', 
                          height: '100%', 
                          backgroundColor: 'rgba(255, 255, 255, 0.7)', 
                          display: 'flex', 
                          justifyContent: 'center', 
                          alignItems: 'center',  
                          zIndex: 1,
                          flexDirection: 'column'
                        }}
                      >
                        <CircularProgress size={40} sx={{ mb: 2 }} />
                        <Typography variant="body2" color="text.secondary">
                          {t("payerForm.refreshingData")}
                        </Typography>
                      </Box>
                    )}                  
                    <AccountingTable
                      modelList={accountingList}
                      setModelList={setAccountingList}
                      canSearch={true}
                      canSearchPlaceholder={t("accountingsTable.searchPlaceholder")}
                      entriesPerPage={{ defaultValue: pageSize }}
                      setPageState={setPage}
                      setPageSizeState={setPageSize}
                      apiPagination={true}
                      apiPageTotal={pageTotal}
                      apiRowsTotal={rowsTotal}
                      apiPageCurrentIndex={page}
                      setModelFilter={setFilterModel}
                      isEmbedded={true}
                    />
                  </Box>
                )}
                <FormActionButtons showSubmitButton={false} closeAction={onCloseAction} />
              </Form>
            );
          }}
        </FormikForm>
      </Box>
    </>
  );
};
export default PayerForm;